import { getStoredReferralCode } from "./referralStorage";
import { sanitizePersonName } from "./sanitize";

// Keep storage key identical to LotteryPanel so state is shared
const TELEGRAM_LOGIN_STORAGE_KEY = "lottery.telegramLoginState";

export const TELEGRAM_LOGIN_STATE_EVENT = "lottery:telegram-login-state";

export function notifyTelegramLoginStateChange(): void {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }
  try {
    // Lightweight client log for telemetry
    console.debug?.("[telegram-login] state change notified");
  } catch {}
  const dispatch = () => {
    try {
      if (typeof window.CustomEvent === "function") {
        window.dispatchEvent(new CustomEvent(TELEGRAM_LOGIN_STATE_EVENT));
      } else {
        window.dispatchEvent(new Event(TELEGRAM_LOGIN_STATE_EVENT));
      }
    } catch {
      window.dispatchEvent(new Event(TELEGRAM_LOGIN_STATE_EVENT));
    }
  };
  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => dispatch());
  } else {
    window.setTimeout(dispatch, 0);
  }
}

type TelegramWebAppUserPayload = {
  id?: number | string;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
  [key: string]: unknown;
};

type TelegramWebAppInitDataUnsafe = {
  user?: TelegramWebAppUserPayload | null;
  hash?: string | null;
  auth_date?: number | string | null;
  [key: string]: unknown;
};

type TelegramLoginUserState = {
  id: number | null;
  username: string | null;
  firstName: string | null;
  channelMember: boolean | null;
};

type TelegramLoginState = {
  status: "idle" | "loading" | "success" | "error";
  displayName: string | null;
  error: string | null;
  user: TelegramLoginUserState | null;
  token: string | null;
  tokenExpiresAt: number | null;
};

let telegramWriteAccessRequested = false;

function requestTelegramWriteAccess(webApp: TelegramWebApp | null | undefined): void {
  if (!webApp) return;
  if (telegramWriteAccessRequested) return;
  const request = webApp.requestWriteAccess;
  if (typeof request !== "function") return;

  const supportsRequest =
    typeof webApp.isVersionAtLeast === "function" ? webApp.isVersionAtLeast("6.9") : true;
  if (!supportsRequest) return;

  telegramWriteAccessRequested = true;

  try {
    console.debug?.("[telegram-login] requesting write access");
  } catch {}

  let settled = false;
  const handleResult = (granted: unknown) => {
    if (settled) return;
    settled = true;
    try {
      console.debug?.("[telegram-login] write access response", { granted: Boolean(granted) });
    } catch {}
  };

  try {
    const maybePromise = request.call(webApp, (granted) => handleResult(granted));
    if (maybePromise && typeof (maybePromise as PromiseLike<unknown>).then === "function") {
      (maybePromise as PromiseLike<unknown>)
        .then((result) => handleResult(typeof result === "boolean" ? result : undefined))
        .catch((error) => {
          try {
            console.warn?.("[telegram-login] write access request rejected", error);
          } catch {}
        });
    }
  } catch (error) {
    try {
      console.warn?.("[telegram-login] write access request threw", error);
    } catch {}
  }
}

function persistTelegramLoginState(state: TelegramLoginState | null): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const storage = window.localStorage;
    if (!storage) return;
    if (!state || state.status !== "success" || !state.user) {
      storage.removeItem(TELEGRAM_LOGIN_STORAGE_KEY);
      notifyTelegramLoginStateChange();
      return;
    }
    const safeDisplayName =
      typeof state.displayName === "string" && state.displayName.trim().length > 0
        ? sanitizePersonName(state.displayName)
        : null;
    const safeFirstName =
      typeof state.user.firstName === "string" && state.user.firstName.trim().length > 0
        ? sanitizePersonName(state.user.firstName)
        : null;
    const safeUsername =
      typeof state.user.username === "string" && state.user.username.trim().length > 0
        ? state.user.username.trim().slice(0, 64)
        : null;
    const safeToken =
      typeof state.token === "string" && state.token.trim().length > 0
        ? state.token.trim().slice(0, 512)
        : null;
    const safeTokenExpiresAt =
      typeof state.tokenExpiresAt === "number" && Number.isFinite(state.tokenExpiresAt)
        ? Math.max(0, Math.trunc(state.tokenExpiresAt))
        : null;
    const payload = {
      displayName: safeDisplayName,
      token: safeToken,
      tokenExpiresAt: safeTokenExpiresAt,
      user: {
        id: state.user.id,
        username: safeUsername,
        firstName: safeFirstName,
        channelMember:
          state.user.channelMember === true
            ? true
            : state.user.channelMember === false
              ? false
              : null,
      },
    };
    storage.setItem(TELEGRAM_LOGIN_STORAGE_KEY, JSON.stringify(payload));
    notifyTelegramLoginStateChange();
  } catch {
    // non-fatal
  }
}

function hasValidStoredSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(TELEGRAM_LOGIN_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { token?: unknown; tokenExpiresAt?: unknown } | null;
    const token = typeof parsed?.token === "string" && parsed.token.trim().length > 0 ? parsed.token : null;
    const expiresAt = typeof parsed?.tokenExpiresAt === "number" && Number.isFinite(parsed.tokenExpiresAt)
      ? Math.trunc(parsed.tokenExpiresAt)
      : null;
    if (!token) return false;
    if (expiresAt && Date.now() > expiresAt) return false;
    return true;
  } catch {
    return false;
  }
}

function resolveTelegramWebAppInitData(webApp: TelegramWebApp | null | undefined): string | null {
  if (!webApp) return null;
  try {
    console.debug?.("[telegram-login] resolving initData from WebApp context...");
  } catch {}
  const initDataUnsafe = (webApp as { initDataUnsafe?: TelegramWebAppInitDataUnsafe | null }).initDataUnsafe ?? null;
  const unsafeUser = initDataUnsafe?.user ?? null;
  const hasUserPayload = Boolean(unsafeUser && typeof unsafeUser === "object");
  const hash = typeof initDataUnsafe?.hash === "string" ? initDataUnsafe.hash.trim() : "";

  const initDataRaw = typeof webApp.initData === "string" ? webApp.initData.trim() : "";
  if (initDataRaw && hasUserPayload) {
    try {
      console.debug?.(
        "[telegram-login] WebApp.initData present",
        { length: initDataRaw.length, hasUserPayload }
      );
    } catch {}
    const attemptResolve = (raw: string): string | null => {
      try {
        const params = new URLSearchParams(raw);
        if (!params.has("auth_date")) return null;
        if (!params.has("hash") && hash) {
          params.set("hash", hash);
          return params.toString();
        }
        return raw;
      } catch {
        return null;
      }
    };

    const resolved = attemptResolve(initDataRaw);
    if (resolved) return resolved;

    if (hash) {
      const trailingChar = initDataRaw.slice(-1);
      const joiner = trailingChar === "?" || trailingChar === "&" ? "" : initDataRaw.includes("?") ? "&" : "?";
      const withHash = `${initDataRaw}${joiner}hash=${encodeURIComponent(hash)}`;
      const resolvedWithHash = attemptResolve(withHash);
      if (resolvedWithHash) return resolvedWithHash;
    }
  }

  if (!hasUserPayload || !hash || !initDataUnsafe) return null;
  try {
    console.debug?.("[telegram-login] building initData from initDataUnsafe with hash");
  } catch {}

  const params = new URLSearchParams();
  Object.entries(initDataUnsafe).forEach(([key, value]) => {
    if (value == null || key === "hash") return;
    if (typeof value === "object") {
      try {
        params.set(key, JSON.stringify(value));
      } catch {
        params.set(key, String(value));
      }
    } else {
      params.set(key, String(value));
    }
  });
  if (!params.has("auth_date")) return null;
  params.set("hash", hash);
  return params.toString();
}

function extractInitDataFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const searchParams = new URLSearchParams(window.location.search);
  const searchInitData = searchParams.get("tgWebAppData");
  let initDataCandidate = typeof searchInitData === "string" ? searchInitData.trim() : "";
  if (!initDataCandidate && window.location.hash) {
    const hashValue = window.location.hash.replace(/^#/, "");
    const hashQuery = hashValue.includes("?") ? hashValue.slice(hashValue.indexOf("?") + 1) : hashValue;
    if (hashQuery) {
      const hashParams = new URLSearchParams(hashQuery);
      const hashInitData = hashParams.get("tgWebAppData");
      if (typeof hashInitData === "string" && hashInitData.trim()) {
        initDataCandidate = hashInitData.trim();
      }
    }
  }
  return initDataCandidate || null;
}

export async function bootstrapTelegramMiniAppLogin(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    console.debug?.("[telegram-login] bootstrap started");
  } catch {}
  const telegram = (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp ?? null;
  requestTelegramWriteAccess(telegram);
  if (hasValidStoredSession()) {
    try { console.debug?.("[telegram-login] valid session present; skipping bootstrap"); } catch {}
    notifyTelegramLoginStateChange();
    return;
  }

  // 1) Prefer explicit tgWebAppData passed when opened via openLink
  let initData = extractInitDataFromUrl();
  try {
    if (initData) console.debug?.("[telegram-login] found tgWebAppData in URL", { length: initData.length });
  } catch {}

  // 2) Fall back to WebApp.initData when running inside Telegram
  if (!initData) {
    initData = resolveTelegramWebAppInitData(telegram);
  }

  if (!initData) return;

  const referralCode = getStoredReferralCode();
  const payload = referralCode ? { webAppInitData: initData, referralCode } : { webAppInitData: initData };

  try {
    console.debug?.("[telegram-login] posting /api/telegram-login", { referral: Boolean(referralCode) });
    const response = await fetch("/api/telegram-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    console.debug?.("[telegram-login] response status", response.status);
    const body = (await response.json().catch(() => null)) as
      | {
          ok?: boolean;
          user?: { username?: string | null; firstName?: string | null; id?: number; channelMember?: unknown } | null;
          session?: { token?: string | null; expiresAt?: number | null } | null;
        }
      | null;
    if (!response.ok || !body?.ok || !body.user) {
      console.warn?.("[telegram-login] unexpected response", { ok: response.ok, body });
      return;
    }

    const user = body.user;
    const username = typeof user.username === "string" && user.username.trim().length > 0
      ? `@${user.username.replace(/^@/, "")}`
      : null;
    const rawFirstName = typeof user.firstName === "string" ? user.firstName : null;
    const firstName = sanitizePersonName(rawFirstName);
    const displayName = firstName ?? null;
    const rawChannelMember = (user as { channelMember?: unknown }).channelMember;
    const channelMember = rawChannelMember === true ? true : rawChannelMember === false ? false : null;
    const sessionToken = typeof body.session?.token === "string" && body.session.token.trim().length > 0
      ? body.session.token.trim().slice(0, 512)
      : null;
    const expiresAtRaw = body.session?.expiresAt;
    const expiresAt = typeof expiresAtRaw === "number" && Number.isFinite(expiresAtRaw)
      ? Math.max(0, Math.trunc(expiresAtRaw))
      : null;
    if (!sessionToken) return;

    const state: TelegramLoginState = {
      status: "success",
      displayName,
      error: null,
      user: { id: typeof user.id === "number" ? Math.trunc(user.id) : null, username, firstName, channelMember },
      token: sessionToken,
      tokenExpiresAt: expiresAt,
    };
    persistTelegramLoginState(state);
    console.debug?.("[telegram-login] login persisted", {
      id: state.user?.id,
      uname: state.user?.username,
      expiresAt: state.tokenExpiresAt,
    });
  } catch {
    // ignore, silent bootstrap only
  }
}
